import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import {
  checkInRegistration,
  createRegistration,
  deleteRegistration,
  getRegistrationById,
  listRegistrations,
  undoCheckIn,
  updateRegistration
} from '../services/registrationsService'

const DEFAULT_STALE_TIME = 1000 * 60 * 2
const DEFAULT_CACHE_TIME = 1000 * 60 * 10

export const registrationsKeys = {
  all: ['event_registrations'],
  lists: () => [...registrationsKeys.all, 'list'],
  list: (options = {}) => [...registrationsKeys.lists(), options],
  details: () => [...registrationsKeys.all, 'detail'],
  detail: (registrationId, options = {}) => [
    ...registrationsKeys.details(),
    registrationId,
    options
  ]
}

const patchRegistrationInListCaches = (queryClient, registrationId, patch) => {
  const listQueries = queryClient.getQueriesData({
    queryKey: registrationsKeys.lists()
  })

  listQueries.forEach(([queryKey, cached]) => {
    if (!cached?.data || !Array.isArray(cached.data)) return
    queryClient.setQueryData(queryKey, {
      ...cached,
      data: cached.data.map((item) =>
        item.id === registrationId ? { ...item, ...patch } : item
      )
    })
  })

  const detailQueries = queryClient.getQueriesData({
    queryKey: registrationsKeys.details()
  })

  detailQueries.forEach(([queryKey, data]) => {
    if (!data || data.id !== registrationId) return
    queryClient.setQueryData(queryKey, { ...data, ...patch })
  })
}

const upsertRegistrationInCache = (queryClient, registration) => {
  const detailQueries = queryClient.getQueriesData({
    queryKey: registrationsKeys.details()
  })

  detailQueries.forEach(([queryKey, data]) => {
    if (data && data.id !== registration.id) return
    queryClient.setQueryData(queryKey, registration)
  })

  const listQueries = queryClient.getQueriesData({
    queryKey: registrationsKeys.lists()
  })

  listQueries.forEach(([queryKey, data]) => {
    if (!Array.isArray(data)) return
    const existingIndex = data.findIndex((item) => item.id === registration.id)
    if (existingIndex === -1) {
      queryClient.setQueryData(queryKey, [registration, ...data])
      return
    }

    const next = [...data]
    next[existingIndex] = registration
    queryClient.setQueryData(queryKey, next)
  })
}

const subscribeToRegistrations = (queryClient) => {
  const channel = supabase.channel('event-registrations-realtime')
  const handleRealtimePayload = (payload) => {
    if (!payload?.new) return
    upsertRegistrationInCache(queryClient, payload.new)
  }

  channel
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'event_registrations' },
      handleRealtimePayload
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'event_registrations' },
      handleRealtimePayload
    )
    .subscribe()

  return () => {
    supabase.removeChannel(channel)
  }
}

export const useRegistrations = (options = {}, config = {}) => {
  const {
    enabled = true,
    realtime = false,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!realtime) return undefined
    return subscribeToRegistrations(queryClient)
  }, [queryClient, realtime])

  return useQuery({
    queryKey: registrationsKeys.list(options),
    queryFn: () => listRegistrations(options),
    enabled,
    staleTime,
    cacheTime
  })
}

export const useRegistration = (registrationId, options = {}, config = {}) => {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config

  return useQuery({
    queryKey: registrationsKeys.detail(registrationId, options),
    queryFn: () => getRegistrationById(registrationId, options),
    enabled: enabled && Boolean(registrationId),
    staleTime,
    cacheTime
  })
}

export const useCreateRegistration = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload) => createRegistration(payload, options),
    onSuccess: (data) => {
      queryClient.setQueryData(registrationsKeys.detail(data.id, options), data)
      queryClient.invalidateQueries({ queryKey: registrationsKeys.lists() })
    }
  })
}

export const useUpdateRegistration = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ registrationId, updates }) =>
      updateRegistration(registrationId, updates, options),
    onMutate: async ({ registrationId, updates }) => {
      await queryClient.cancelQueries({ queryKey: registrationsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: registrationsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: registrationsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.map((item) =>
            item.id === registrationId ? { ...item, ...updates } : item
          )
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== registrationId) return
        queryClient.setQueryData(queryKey, { ...data, ...updates })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: registrationsKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: registrationsKeys.detail(variables.registrationId)
      })
    }
  })
}

export const useCancelRegistration = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (registrationId) =>
      updateRegistration(registrationId, { status: 'canceled' }, options),
    onMutate: async (registrationId) => {
      await queryClient.cancelQueries({ queryKey: registrationsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: registrationsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: registrationsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.map((item) =>
            item.id === registrationId
              ? { ...item, status: 'canceled' }
              : item
          )
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== registrationId) return
        queryClient.setQueryData(queryKey, { ...data, status: 'canceled' })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _registrationId, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, registrationId) => {
      queryClient.invalidateQueries({ queryKey: registrationsKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: registrationsKeys.detail(registrationId)
      })
    }
  })
}

const useAttendanceMutation = (mutationFn, options = {}, { checkIn = true } = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (registrationId) => {
      const result = await mutationFn(registrationId, options)
      if (result?.error) {
        throw result.error
      }
      return result?.data
    },
    onMutate: async (registrationId) => {
      await queryClient.cancelQueries({ queryKey: registrationsKeys.lists() })

      const previousLists = queryClient.getQueriesData({
        queryKey: registrationsKeys.lists()
      })

      const isCheckIn = checkIn
      const optimisticPatch = isCheckIn
        ? { attended: true, attendance_verified_at: new Date().toISOString() }
        : { attended: false, attendance_verified_at: null }

      patchRegistrationInListCaches(queryClient, registrationId, optimisticPatch)

      return { previousLists }
    },
    onError: (_error, _registrationId, context) => {
      context?.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSuccess: (registration, registrationId) => {
      if (registration) {
        patchRegistrationInListCaches(queryClient, registrationId, registration)
      }
    },
    onSettled: (_data, _error, registrationId) => {
      queryClient.invalidateQueries({ queryKey: registrationsKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: registrationsKeys.detail(registrationId, options)
      })
    }
  })
}

export const useCheckInRegistration = (options = {}) =>
  useAttendanceMutation(checkInRegistration, options, { checkIn: true })

export const useUndoCheckIn = (options = {}) =>
  useAttendanceMutation(undoCheckIn, options, { checkIn: false })

export const useDeleteRegistration = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (registrationId) => deleteRegistration(registrationId, options),
    onMutate: async (registrationId) => {
      await queryClient.cancelQueries({ queryKey: registrationsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: registrationsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: registrationsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.filter((item) => item.id !== registrationId)
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== registrationId) return
        queryClient.removeQueries({ queryKey, exact: true })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _registrationId, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, registrationId) => {
      queryClient.invalidateQueries({ queryKey: registrationsKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: registrationsKeys.detail(registrationId)
      })
    }
  })
}
